import { Eyebrow } from '../concept/eyebrow';

/**
 * One of the four figures across the top of the coach overview.
 *
 * The reference deck colour-codes these blue / red / amber / green down the
 * left edge. That is the traffic light the concept drops, and for the reason
 * the accessibility work established — four hues in one band cannot be made
 * to separate. The emphasis that survives is sage for the one tile a coach is
 * meant to act on, and ink for the rest, so exactly one thing pulls the eye.
 */
export const KpiTile: React.FC<{
  label: string;
  value: number | string;
  caption: string;
  /** The tile that carries the next action. At most one. */
  accent?: boolean;
  onClick?: () => void;
  /**
   * The tile whose clients the list below is showing. A ring, not a fill:
   * the fill is the accent's, and two filled tiles would be two things
   * pulling the eye.
   */
  pressed?: boolean;
  testId?: string;
}> = ({ label, value, caption, accent, onClick, pressed, testId }) => {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      {...(onClick ? { type: 'button' as const, onClick, 'aria-pressed': Boolean(pressed) } : {})}
      data-testid={testId}
      // Two to a row on a phone, so the padding and figure come down a step
      // there; four big tiles stacked one above the other pushed the list
      // they summarise below the fold.
      className={`relative flex min-w-0 flex-col gap-2 rounded-lg border p-3.5 text-left transition-colors sm:p-5 ${
        accent ? 'border-primary bg-accent' : 'border-border bg-card'
      } ${pressed ? 'ring-2 ring-inset ring-primary' : ''} ${
        onClick ? 'hover:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring' : ''
      }`}
    >
      <Eyebrow tone={accent || pressed ? 'primary' : 'muted'} className="truncate text-[10px] sm:text-[9.5px]">
        {label}
      </Eyebrow>
      <div className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <span className="tabular text-[24px] font-semibold leading-none tracking-[-0.02em] sm:text-[26px]">
          {value}
        </span>
        <span className="text-[11px] text-muted-foreground">{caption}</span>
      </div>
    </Tag>
  );
};
